/**
 * `/v1/media` — image, doc and artwork **metadata**. No file bytes pass through the API.
 *
 * ## Where this schema stops, and why the line is exactly here
 *
 * The route also refuses a `storageKey` that is not under `org/{orgId}/`, and **that rule
 * is not in this file**. It cannot be: it compares the request against *the caller's
 * organization*, which is a fact about the session rather than about the body. It stays
 * in the op, which is the division R7 draws — schemas own shape, the ops layer owns
 * everything stateful.
 *
 * It is the sharpest example in the catalogue of why that line matters. §12 puts the
 * tenancy **in the object key**, because object storage has no row-level security to fall
 * back on — so that check is a security boundary, and a schema that appeared to cover it
 * while comparing against nothing would be worse than no schema at all.
 */
import { z } from "zod";
import { MEDIA_KINDS } from "../catalog.js";
/**
 * The route's own messages, carried across verbatim — including the fact that **three
 * different missing fields share one sentence**.
 *
 * The shipped route checks `storageKey === "" || mime === "" || bytes < 0` together and
 * answers once, so all three rules below carry the identical message. Splitting them into
 * three friendlier sentences would be better copy and a moved wire, and the wire wins:
 * these strings are what an integrator matches on.
 */
const REQUIRED_MESSAGE = "storageKey, mime and bytes are required.";
const KIND_MESSAGE = `kind must be one of: ${MEDIA_KINDS.join(", ")}.`;
export const createMediaSchema = z
    .object({
    kind: z.enum(MEDIA_KINDS, KIND_MESSAGE),
    storageKey: z.string(REQUIRED_MESSAGE).min(1, REQUIRED_MESSAGE),
    mime: z.string(REQUIRED_MESSAGE).min(1, REQUIRED_MESSAGE),
    /** `bytes < 0` is the route's rule; zero is a legal size and is accepted. */
    bytes: z.number(REQUIRED_MESSAGE).int(REQUIRED_MESSAGE).min(0, REQUIRED_MESSAGE),
    /**
     * Optional: media may belong to the organization rather than to a product — the
     * `GET` half lists both, which is why `media.product_id` is nullable.
     */
    productId: z.uuid().optional(),
    /** Per-language alt text. `screens.md` §4 requires it per language, so it is a map. */
    alt: z.record(z.string(), z.string()).optional(),
    position: z.number().int().min(0).default(0),
})
    .describe("POST /v1/media — metadata only; bytes go straight to object storage.");
const MIME_MESSAGE = "mime is required — it is what the product records about this file, and the object " +
    "key's extension is derived from it.";
const BYTES_MESSAGE = "bytes must be a whole number of bytes, zero or more. It is the size you are about to " +
    "upload; the file itself never passes through this API.";
/**
 * `POST /v1/products/{id}/media` — ask for a presigned PUT (§5, §12).
 *
 * ## There is no `storageKey` member, and its absence is the security property
 *
 * {@link createMediaSchema} above takes one, because c29's metadata half had to: no
 * presign existed, so a caller that had somehow put an object in the bucket had to be able
 * to name it. The op refuses a key outside `org/{orgId}/`, and `ops/media.ts` calls that
 * *"the one check with nothing behind it"* — accurately.
 *
 * **This request cannot name a key at all.** The server derives it from the resolved
 * session's organisation, the product, a fresh media id and an extension from a closed
 * mime map, so *"a presign pointed at another tenant's prefix"* is not a request that gets
 * refused — it is a request that cannot be written. That is the stronger of the two
 * postures and it is available here only because a presign is issued rather than accepted.
 *
 * `filename` is carried for the person, not for the path: it is recorded in `media.meta`
 * so a dashboard can show *"gs1-licence.pdf"*, and it never reaches the key. A filename
 * that did reach the key is the traversal `orgScopedKey` exists to stop, and a rule that
 * relies on the builder catching it is a rule two files away from its subject.
 */
export const presignMediaSchema = z
    .object({
    kind: z.enum(MEDIA_KINDS, KIND_MESSAGE),
    mime: z.string(MIME_MESSAGE).min(1, MIME_MESSAGE),
    /**
     * The size the client says it is about to upload.
     *
     * Declared rather than verified — the bytes never pass through this API, so nothing
     * here can know. It is stored because `media.bytes` is `NOT NULL` and because a
     * declared size is what a quota check will read; the commit that adds a quota is the
     * commit that has to decide what to do about a client that lied.
     */
    bytes: z.number(BYTES_MESSAGE).int(BYTES_MESSAGE).min(0, BYTES_MESSAGE),
    /** Shown to a person; never part of the object key. See the docblock. */
    filename: z.string().max(255).optional(),
    alt: z.record(z.string(), z.string()).optional(),
    position: z.number().int().min(0).default(0),
})
    .describe("POST /v1/products/{id}/media — returns a presigned PUT.");
/** `GET /v1/media?productId=` — the one filter the route accepts. */
export const mediaListQuerySchema = z
    .object({ productId: z.uuid().optional() })
    .describe("GET /v1/media");
//# sourceMappingURL=media.js.map